import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { createApiClient } from '../../services/api/client'
import {
  conversationOwnershipCommandSchema,
  conversationOwnershipStateSchema,
  conversationSmsRouteSchema,
  conversationCommandErrorSchema,
  staffSmsCommandResultSchema,
  type ConversationOwnershipState,
  type ConversationSmsRoute,
} from './conversation-ownership-contracts'

export type ConversationOwnershipRepository = {
  getState: (
    tenantId: string,
    aggregateId: string,
    signal?: AbortSignal
  ) => Promise<ConversationOwnershipState>
  getSmsRoute: (
    tenantId: string,
    aggregateId: string,
    signal?: AbortSignal
  ) => Promise<ConversationSmsRoute>
  transition: (
    tenantId: string,
    aggregateId: string,
    action: 'TAKE_OVER' | 'RETURN_TO_FLIO',
    expectedRevision: number,
    commandId: string,
    route?: ConversationSmsRoute
  ) => Promise<ConversationOwnershipState>
  sendStaffSms: (
    tenantId: string,
    aggregateId: string,
    input: {
      expectedOwnershipRevision: number
      idempotencyKey: string
      routeRef: string
      routeRevision: number
      content: string
    }
  ) => Promise<void>
}

function base(tenantId: string, aggregateId: string) {
  return `/dashboard/clinics/${encodeURIComponent(tenantId)}/interaction-aggregates/${encodeURIComponent(aggregateId)}`
}

export function createConversationOwnershipRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): ConversationOwnershipRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  return {
    getState(tenantId, aggregateId, signal) {
      return client.request(`${base(tenantId, aggregateId)}/ownership`, {
        method: 'GET',
        signal,
        responseSchema: conversationOwnershipStateSchema,
      })
    },
    getSmsRoute(tenantId, aggregateId, signal) {
      return client.request(`${base(tenantId, aggregateId)}/sms-route`, {
        method: 'GET',
        signal,
        responseSchema: conversationSmsRouteSchema,
        errorSchema: conversationCommandErrorSchema,
      })
    },
    async transition(
      tenantId,
      aggregateId,
      action,
      expectedRevision,
      commandId,
      route
    ) {
      const result = await client.request(
        `${base(tenantId, aggregateId)}/ownership`,
        {
          method: 'POST',
          body:
            action === 'TAKE_OVER' && route
              ? {
                  action,
                  expectedRevision,
                  commandId,
                  routeRef: route.routeRef,
                  routeRevision: route.routeRevision,
                }
              : { action, expectedRevision, commandId },
          responseSchema: conversationOwnershipCommandSchema,
          errorSchema: conversationCommandErrorSchema,
        }
      )
      const { replayClassification, ...state } = result
      void replayClassification
      return state
    },
    async sendStaffSms(tenantId, aggregateId, input) {
      const result = await client.request(
        `${base(tenantId, aggregateId)}/messages`,
        {
          method: 'POST',
          body: input,
          responseSchema: staffSmsCommandResultSchema,
          errorSchema: conversationCommandErrorSchema,
        }
      )
      if (
        result.dispatchState === 'FAILED_TERMINAL' ||
        result.dispatchState === 'RECONCILIATION_REQUIRED'
      ) {
        throw new Error(result.dispatchState)
      }
    },
  }
}
